import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import { Category } from "@insite/client-framework/Services/CategoryService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCategoriesDataView,
    getSubCategoryIds,
} from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import LinkAnimated, { LinkAnimatedProps } from "@waytek/MobiusOverrides/Link/LinkAnimated";
import { mediaQueriesBP } from "@waytek/Utilities/variables";
import { HorizontalDividerWidget } from "@waytek/Widgets/Basic/HorizontalDivider";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    title = "title",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    categoriesDataView: getCategoriesDataView(state),
    topLevelCategoryIds: getSubCategoryIds(state, emptyGuid),
});

const mapDispatchToProps = {
    loadCategories,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme>;

export interface CategoryListStyles {
    wrapper?: GridContainerProps;
    titleItem?: GridItemProps;
    title?: TypographyProps;
    dividerItem?: GridItemProps;
    categoriesItem?: GridItemProps;
    categoryWrapper?: GridContainerProps;
    categoryItem?: GridItemProps;
    categoryLink?: LinkAnimatedProps;
}

export const categoryListStyles: CategoryListStyles = {
    wrapper: {
        gap: 10,
    },
    titleItem: {
        width: 12,
    },
    title: {
        as: "h2",
        variant: "h2",
        mergeCss: true,
        css: css`
            margin-bottom: 0;
        `,
    },
    dividerItem: {
        width: 12,
        css: css`
            &&& {
                margin-bottom: 13px;
            }
        `,
    },
    categoriesItem: {
        width: [6, 6, 4, 3, 3],
    },
    categoryWrapper: {
        gap: 20,
        offsetProps: {
            as: "ul",
        },
    },
    categoryItem: {
        width: 12,
        as: "li",
    },
    categoryLink: {
        icon: {
            iconProps: {
                src: ChevronRight,
                size: 20,
            },
            position: "right",
        },
        typographyProps: {
            css: css`
                font-size: 18px;
                white-space: break-spaces;

                ${mediaQueriesBP.maxMD} {
                    font-size: 14px;
                }
            `,
        },
    },
};

const styles = categoryListStyles;

export const CategoryList: React.FC<Props> = ({
    categoriesDataView,
    topLevelCategoryIds,
    loadCategories,
    fields: { title },
}) => {
    const [windowSize, setWindowSize] = React.useState(4);
    React.useEffect(() => {
        if (!categoriesDataView.isLoading && !categoriesDataView.value) {
            loadCategories();
        }
    });

    React.useEffect(() => {
        const handleResize = () => {
            const desktop = "(min-width: 1024px)";
            const tablet = "(min-width: 768px) and (max-width: 1023px)";
            const colSize2 = window.matchMedia(desktop).matches ? 4 : window.matchMedia(tablet).matches ? 3 : 2;
            setWindowSize(colSize2);
        };
        window.addEventListener("resize", handleResize);
        handleResize();
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    let childrenData = null;

    if (categoriesDataView.isLoading) {
        childrenData = <></>;
    } else if (!categoriesDataView.value || !topLevelCategoryIds) {
        childrenData = <></>;
    } else {
        const onlyTopCategories = categoriesDataView.value.filter(o => topLevelCategoryIds.indexOf(o.id) > -1);
        let extraItems = onlyTopCategories.length % windowSize;
        const itemsByCol = (onlyTopCategories.length - extraItems) / windowSize;

        const allItems = [...onlyTopCategories];
        const cols: Category[][] = [];
        for (let colIdx = 0; colIdx < windowSize; colIdx++) {
            const items: Category[] = [];
            for (let idx = 0; idx < itemsByCol; idx++) {
                items.push(allItems.splice(0, 1)[0]);
            }
            if (extraItems > 0) {
                items.push(allItems.splice(0, 1)[0]);
                extraItems--;
            }
            cols.push(items);
        }

        childrenData = (
            <>
                {cols.map((col, colIdx) => (
                    <GridItem
                        // eslint-disable-next-line react/no-array-index-key
                        key={colIdx}
                        {...styles.categoriesItem}
                    >
                        <GridContainer {...styles.categoryWrapper}>
                            {col.map((child, idx) => (
                                <GridItem
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={colIdx}
                                    {...styles.categoryItem}
                                >
                                    <LinkAnimated href={child.path} {...styles.categoryLink}>
                                        {child.name}
                                    </LinkAnimated>
                                </GridItem>
                            ))}
                        </GridContainer>
                    </GridItem>
                ))}
            </>
        );
    }

    return (
        <GridContainer {...styles.wrapper} className="ProductCategoryList">
            <GridItem {...styles.titleItem}>
                <Typography data-test-selector="ProductCategoryListTitle" {...styles.title}>
                    {title}
                </Typography>
            </GridItem>

            <GridItem {...styles.dividerItem}>
                <HorizontalDividerWidget
                    fields={{ color: "#b89e7d" }}
                    sizeMobile={3}
                    sizeDesktop={3}
                ></HorizontalDividerWidget>
            </GridItem>

            {childrenData}
        </GridContainer>
    );
    // }
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(CategoryList)),
    definition: {
        group: "Basic",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                displayName: "Title",
                fieldType: "General",
                defaultValue: "Product Categories",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
